/**
 * Post-login landing dashboards.
 *
 * Keys match `App\Enums\Dashboard` and the role `default_dashboard` field.
 * The backend resolver (`DefaultDashboardResolver`) is the source of the preferred
 * key for the active-business primary role. This module maps that key to a named
 * Vue route. Whether the user may open that route is decided by the sidebar gates
 * in `routeAccess.ts` — a stored key never grants extra access, and a
 * client-supplied URL is never followed.
 */

export const FALLBACK_DASHBOARD_KEY = 'general' as const

export const DASHBOARD_ROUTE_MAP = {
  general: { routeName: 'dashboard', moduleSlug: 'dashboard' },
  inventory: { routeName: 'inventory-dashboard', moduleSlug: 'inventory' },
  finance: { routeName: 'finance-index', moduleSlug: 'finance' },
  sales: { routeName: 'sales-list', moduleSlug: 'sales' },
} as const

export type DashboardKey = keyof typeof DASHBOARD_ROUTE_MAP

export const DASHBOARD_KEYS = Object.keys(DASHBOARD_ROUTE_MAP) as DashboardKey[]

/** Accept only known dashboard keys. URLs, numbers, and unknown values are ignored. */
export function parseDashboardKey(value: unknown): DashboardKey | null {
  if (typeof value !== 'string') return null

  const key = value.trim()
  if (!key || key.includes('://') || key.includes('/') || !(key in DASHBOARD_ROUTE_MAP)) {
    return null
  }

  return key as DashboardKey
}

export function dashboardKeyForRouteName(routeName: string | null | undefined): DashboardKey | null {
  if (!routeName) return null

  const match = DASHBOARD_KEYS.find((key) => DASHBOARD_ROUTE_MAP[key].routeName === routeName)
  return match ?? null
}
